import { useState } from "react";
import { X } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitIconButton } from "@/app/routes/dashboard/components/kit/IconButton";
import { KitSegmentedControl } from "@/app/routes/dashboard/components/kit/SegmentedControl";
import {
  DEFAULT_WINDOW_WEEKS,
  SIGNAL_KIND_OPTIONS,
  formatFullDate,
  isEvergreenKind,
} from "./signalMeta";
import KindSelect from "./KindSelect";
import LifetimeFields, {
  acceptWeeksInput,
  committedWeeksInput,
  resolveLifetimeChoice,
} from "./LifetimeFields";

export interface CreateSignalDraft {
  kind: string;
  title: string;
  body: string;
  activate: boolean;
  validUntil: string | null;
}

const BODY_MAX = 280;

type Mode = "write" | "m7";

const MODES: { value: Mode; label: string }[] = [
  { value: "write", label: "Write it" },
  { value: "m7", label: "Let M7 write it" },
];

/**
 * The New Signal dialog's content (Signals / New signal, Dialog / Form). Two
 * ways in, chosen at the top:
 *
 * Write it: a person logging what they are seeing is their own curator, so
 * confirming creates an ACTIVE signal (`activate: true`) for the window chosen
 * in LifetimeFields, which is on screen from the start.
 *
 * Let M7 write it (Emad, 1 Oct): paste a link or say what you saw, and M7 files
 * a waiting signal from it in the chat (the agent's SetMarketSignal), for
 * someone to check and accept in Waiting. The form only hands the words over.
 *
 * The page owns the dialog frame (SignalsDialog); `titleId` names it.
 */
export default function CreateSignalForm({
  titleId,
  onCreate,
  onAskM7,
  onCancel,
}: {
  titleId: string;
  onCreate: (draft: CreateSignalDraft) => void;
  /** Hand the link or note to M7; absent where no agent can take it. */
  onAskM7?: (text: string) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<Mode>("write");
  const [kind, setKind] = useState<string>("trend");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [note, setNote] = useState("");
  const [weeksInput, setWeeksInput] = useState(String(DEFAULT_WINDOW_WEEKS));
  const [openEnded, setOpenEnded] = useState(false);

  // Only the evergreen kinds may run open-ended; derived rather than reset in an effect.
  const effectiveOpenEnded = openEnded && isEvergreenKind(kind);
  const validUntil = resolveLifetimeChoice(weeksInput, effectiveOpenEnded);
  const ready = title.trim().length > 0 && body.trim().length > 0;
  const m7 = mode === "m7" && !!onAskM7;

  return (
    <div className="sig-new">
      {/* The grabber shows only where the dialog is a phone sheet. */}
      <span className="sig-new__grab" aria-hidden="true" />
      <header className="sig-new__head">
        <h2 className="sig-new__title" id={titleId}>
          Log a signal
        </h2>
        <KitIconButton icon={X} aria-label="Close" onClick={onCancel} className="sig-new__close" />
      </header>

      <div className="sig-new__body">
        <div className="sig-new__mode">
          {onAskM7 ? (
            <KitSegmentedControl
              variant="lens"
              ariaLabel="How to log it"
              options={MODES}
              value={mode}
              onChange={setMode}
            />
          ) : null}
          <p className="sig-new__intro">
            {m7
              ? "Paste a link, or say what you saw. M7 writes the kind, the headline, the read and a window, and files it in Waiting. You check it there, then accept or decline it."
              : "You're the curator on this one: it starts steering as soon as you create it, for the window you set below."}
          </p>
        </div>

        {m7 ? (
          <label className="sig-field">
            <span className="sig-field__label">A link, or what you saw</span>
            <textarea
              className="sig-input sig-textarea"
              dir="auto"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Paste a link, or describe what you saw."
            />
            <span className="sig-field__hint">
              M7 suggests the window too. You can change any of it before you accept.
            </span>
          </label>
        ) : (
          <div className="sig-new__fields">
            <KindSelect
              value={kind}
              onChange={setKind}
              options={SIGNAL_KIND_OPTIONS}
              id="sig-new-kind"
              label="Kind"
            />

            <label className="sig-field">
              <span className="sig-field__label">Headline</span>
              <input
                className="sig-input"
                type="text"
                dir="auto"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="One line: what is changing?"
              />
            </label>

            <label className="sig-field">
              <span className="sig-field__label">What you're seeing</span>
              <textarea
                className="sig-input sig-textarea"
                dir="auto"
                maxLength={BODY_MAX}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="The read, and why it matters for the work."
              />
              <span className="sig-textarea-count" aria-hidden="true">
                {body.length} / {BODY_MAX}
              </span>
            </label>

            <LifetimeFields
              kind={kind}
              weeksInput={weeksInput}
              openEnded={effectiveOpenEnded}
              onWeeksChange={(raw) => acceptWeeksInput(raw) && setWeeksInput(raw)}
              onWeeksBlur={() =>
                setWeeksInput((w) => committedWeeksInput(w, String(DEFAULT_WINDOW_WEEKS)))
              }
              onOpenEndedChange={setOpenEnded}
              label="How long it steers"
              hint={
                validUntil
                  ? `until ${formatFullDate(validUntil)}`
                  : "until a newer signal replaces it"
              }
            />
          </div>
        )}
      </div>

      <footer className="sig-new__foot">
        <KitButton variant="ghost" onClick={onCancel}>
          Cancel
        </KitButton>
        {m7 ? (
          <KitButton
            variant="primary"
            isDisabled={!note.trim()}
            onClick={() => onAskM7?.(note.trim())}
          >
            Ask M7
          </KitButton>
        ) : (
          <KitButton
            variant="primary"
            isDisabled={!ready}
            onClick={() =>
              onCreate({
                kind,
                title: title.trim(),
                body: body.trim(),
                activate: true,
                validUntil,
              })
            }
          >
            Create signal
          </KitButton>
        )}
      </footer>
    </div>
  );
}
